import { useState } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Edit,
  Plus,
  CheckCircle,
  Layers,
  Package,
  Scale,
  Ruler,
  Calendar,
  User,
  Trash2,
  Play,
  Building2,
  FolderKanban,
} from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { usePlybook, useApprovePlybook, useDeletePly, usePlybookPlies, useZonePositions, useInitializeDefaultZone } from '@/hooks/usePlybooks';
import { PlybookFormDialog } from './PlybookFormDialog';
import { PlyEditor } from './PlyEditor';
import { PlyCard } from './PlyCard';
import { PlyStackViewer } from './PlyStackViewer';
import { PlyImageOverlay } from './PlyImageOverlay';
import { ZoneImageOverlay } from './ZoneImageOverlay';
import { PlybookFileUpload } from './PlybookFileUpload';
import { PlybookLaminationView } from './PlybookLaminationView';
import {
  PLYBOOK_STATUS_LABELS,
  PLYBOOK_STATUS_COLORS,
} from '@/types/plybook';
import type { PlybookPly, PlybookZonePosition } from '@/types/plybook';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface PlybookDetailProps {
  plybookId: string;
  onBack: () => void;
}

// Sortable wrapper for drag & drop
function SortablePlyCard({ ply, zonePositions, onEdit, onDelete }: { ply: PlybookPly; zonePositions: PlybookZonePosition[]; onEdit: () => void; onDelete: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ply.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  
  return (
    <div ref={setNodeRef} style={style}>
      <PlyCard
        zonePositions={zonePositions}
        ply={ply}
        onEdit={onEdit}
        onDelete={onDelete}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}


export function PlybookDetail({ plybookId, onBack }: PlybookDetailProps) {
  const queryClient = useQueryClient();
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  const reorderMutation = useMutation({
    mutationFn: async (updates: { id: string; ply_number: number }[]) => {
      for (const update of updates) {
        await api.from('plybook_plies').update({ ply_number: update.ply_number }).eq('id', update.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plybook-plies', plybookId] });
    },
  });
  
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    
    const oldIndex = plybookPlies.findIndex(p => p.id === active.id);
    const newIndex = plybookPlies.findIndex(p => p.id === over.id);
    
    const reordered = arrayMove(plybookPlies, oldIndex, newIndex);
    const updates = reordered.map((ply, idx) => ({ id: ply.id, ply_number: idx + 1 }));
    reorderMutation.mutate(updates);
  };

  const { data: plybook, isLoading } = usePlybook(plybookId);
  const { data: plybookPlies = [], isLoading: pliesLoading } = usePlybookPlies(plybookId);
    const { data: zonePositions = [] } = useZonePositions(plybookId);
  const approveMutation = useApprovePlybook();
  const deletePlyMutation = useDeletePly();

  const [editFormOpen, setEditFormOpen] = useState(false);
  const [plyEditorOpen, setPlyEditorOpen] = useState(false);
  const [editingPly, setEditingPly] = useState<PlybookPly | null>(null);
  const [deletePlyDialog, setDeletePlyDialog] = useState<{ open: boolean; ply?: PlybookPly }>({
    open: false,
  });
  const [selectedPlyId, setSelectedPlyId] = useState<string>();
  const [selectedZoneId, setSelectedZoneId] = useState<string>();
  const [laminationMode, setLaminationMode] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-muted animate-pulse rounded" />
        <div className="h-64 bg-muted animate-pulse rounded" />
      </div>
    );
  }

  if (!plybook) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Plybook no encontrado</p>
        <Button variant="outline" className="mt-4" onClick={onBack}>
          Volver
        </Button>
      </div>
    );
  }

  const handleApprove = async () => {
    await approveMutation.mutateAsync(plybook.id);
  };

  const handleAddPly = () => {
    setEditingPly(null);
    setPlyEditorOpen(true);
  };

  const handleEditPly = (ply: PlybookPly) => {
    setEditingPly(ply);
    setPlyEditorOpen(true);
  };

  const handleDeletePly = async () => {
    if (deletePlyDialog.ply) {
      await deletePlyMutation.mutateAsync({
        id: deletePlyDialog.ply.id,
        plybookId: plybook.id,
      });
      setDeletePlyDialog({ open: false });
    }
  };

  // Calcular el siguiente número de capa basado en el máximo existente
  const nextPlyNumber = plybookPlies.length
    ? Math.max(...plybookPlies.map(p => p.ply_number)) + 1
    : 1;

  // Modo Laminador - Vista de pantalla completa
  if (laminationMode) {
    return (
      <PlybookLaminationView
        workOrderId=""
        plybookId={plybookId}
        onBack={() => setLaminationMode(false)}
        onComplete={() => setLaminationMode(false)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold">{plybook.name}</h1>
              <Badge className={PLYBOOK_STATUS_COLORS[plybook.status]}>
                {PLYBOOK_STATUS_LABELS[plybook.status]}
              </Badge>
              <Badge variant="outline">v{plybook.version}</Badge>
            </div>
            {plybook.code && (
              <p className="text-sm text-muted-foreground">{plybook.code}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Botón siempre visible para plybooks */}
          <Button
            onClick={() => setLaminationMode(true)}
            className="gap-2 bg-green-600 hover:bg-green-700"
          >
            <Play className="w-4 h-4" />
            Modo Laminador ({plybookPlies.length} capas)
          </Button>
          {plybook.status === 'draft' && (
            <Button
              variant="outline"
              onClick={handleApprove}
              disabled={approveMutation.isPending}
              className="gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Aprobar
            </Button>
          )}
          <Button variant="outline" onClick={() => setEditFormOpen(true)} className="gap-2">
            <Edit className="w-4 h-4" />
            Editar
          </Button>
        </div>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Layers className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{plybook.total_plies}</p>
              <p className="text-xs text-muted-foreground">Capas</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
              <Scale className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {plybook.total_weight_grams?.toFixed(0) || '0'}g
              </p>
              <p className="text-xs text-muted-foreground">Peso Total</p>
            </div>
          </CardContent>
        </Card>

        {plybook.customer && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-purple-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{plybook.customer.name}</p>
                <p className="text-xs text-muted-foreground">Cliente</p>
              </div>
            </CardContent>
          </Card>
        )}

        {plybook.project && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                <FolderKanban className="w-5 h-5 text-cyan-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{plybook.project.name}</p>
                <p className="text-xs text-muted-foreground">Proyecto</p>
              </div>
            </CardContent>
          </Card>
        )}

        {plybook.mold && (
          <Card>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Ruler className="w-5 h-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">{plybook.mold.name}</p>
                <p className="text-xs text-muted-foreground">Molde</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ply List */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Capas de Laminado</h2>
            <Button onClick={handleAddPly} size="sm" className="gap-2">
              <Plus className="w-4 h-4" />
              Agregar Capa
            </Button>
          </div>

          {plybookPlies.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Layers className="w-12 h-12 text-muted-foreground/30 mb-4" />
                <p className="text-muted-foreground mb-4">
                  No hay capas definidas
                </p>
                <Button onClick={handleAddPly} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Agregar primera capa
                </Button>
              </CardContent>
            </Card>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={plybookPlies.map(p => p.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-2">
                  {plybookPlies.map((ply) => (
                    <SortablePlyCard
                      zonePositions={zonePositions}
                      key={ply.id}
                      ply={ply}
                      onEdit={() => handleEditPly(ply)}
                      onDelete={() => setDeletePlyDialog({ open: true, ply })}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Stack Viewer & Image Overlay */}
        <div className="space-y-4">
          <PlyStackViewer
            plies={plybookPlies}
            selectedPlyId={selectedPlyId}
            onSelectPly={(ply) => setSelectedPlyId(ply.id)}
          />

          {/* Zone Position Overlay */}
          {plybook.main_image_url && (
            <ZoneImageOverlay
              imageUrl={plybook.main_image_url}
              zonePositions={zonePositions}
              plybookId={plybook.id}
              selectedZoneId={selectedZoneId}
              onSelectZone={(zoneId) => setSelectedZoneId(zoneId)}
              onZonesChange={() => {}}
            />
          )}

          {/* Metadata */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Información</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {plybook.description && (
                <div>
                  <p className="text-muted-foreground">Descripción</p>
                  <p>{plybook.description}</p>
                </div>
              )}

              {plybook.notes && (
                <div>
                  <p className="text-muted-foreground">Notas</p>
                  <p>{plybook.notes}</p>
                </div>
              )}

              <Separator />

              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>
                  Creado {format(new Date(plybook.created_at), "d MMM yyyy", { locale: es })}
                </span>
              </div>

              {plybook.created_by_profile && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <User className="w-4 h-4" />
                  <span>Por {plybook.created_by_profile.full_name}</span>
                </div>
              )}

              {plybook.approved_by_profile && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="w-4 h-4" />
                  <span>
                    Aprobado por {plybook.approved_by_profile.full_name}
                    {plybook.approved_at && (
                      <> el {format(new Date(plybook.approved_at), "d MMM yyyy", { locale: es })}</>
                    )}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* File Attachments */}
          <PlybookFileUpload plybookId={plybook.id} />
        </div>
      </div>

      {/* Edit Plybook Dialog */}
      <PlybookFormDialog
        open={editFormOpen}
        onOpenChange={setEditFormOpen}
        plybook={plybook}
      />

      {/* Ply Editor Dialog */}
      <PlyEditor
        open={plyEditorOpen}
        onOpenChange={setPlyEditorOpen}
        plybookId={plybook.id}
        ply={editingPly}
        nextPlyNumber={nextPlyNumber}
      />

      {/* Delete Ply Confirmation */}
      <AlertDialog
        open={deletePlyDialog.open}
        onOpenChange={(open) => setDeletePlyDialog({ open })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar capa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará la capa #{deletePlyDialog.ply?.ply_number}. Las
              demás capas serán renumeradas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePly}
              className="bg-destructive text-destructive-foreground"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
